import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Globe } from "lucide-react";
import LanguageSwitcher from "@/components/LanguageSwitcher";

export default function Login() {
  const { t } = useTranslation();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      toast.success(t("auth.welcome"));
      navigate("/");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F4F4F5] flex">
      <div className="hidden lg:flex flex-col justify-between w-1/2 bg-zinc-950 text-white p-12">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-white flex items-center justify-center rounded-sm overflow-hidden">
            <img src="/logo.webp" alt={t("app.name")} className="w-full h-full object-cover scale-150" />
          </div>
          <span className="font-display text-xl font-bold">{t("app.name")}</span>
        </div>
        <div>
          <div className="label-caps text-zinc-500 mb-4">{t("auth.heroLabel")}</div>
          <h1 className="font-display text-5xl font-bold leading-tight tracking-tight">
            {t("auth.heroTitle1")}<br/>
            {t("auth.heroTitle2")}<br/>
            <span className="text-[#0052FF]">{t("auth.heroTitle3")}</span>
          </h1>
          <p className="text-zinc-400 mt-6 max-w-md leading-relaxed">
            {t("auth.heroSub")}
          </p>
        </div>
        <div className="text-xs text-zinc-600 label-caps">© {t("app.name")}</div>
      </div>
      <div className="flex-1 flex flex-col p-8">
        <div className="flex items-center justify-end gap-2 mb-4">
          <span className="inline-flex items-center gap-1.5 text-xs font-medium text-zinc-500" data-testid="login-lang-label">
            <Globe className="w-4 h-4" /> Taal · Kalba
          </span>
          <LanguageSwitcher />
        </div>
        <div className="flex-1 flex items-center justify-center">
        <form onSubmit={submit} className="w-full max-w-sm space-y-6" data-testid="login-form">
          <div>
            <div className="label-caps mb-2">{t("auth.login")}</div>
            <h2 className="font-display text-3xl font-bold tracking-tight">{t("auth.welcome")}</h2>
            <p className="text-zinc-500 text-sm mt-2">{t("auth.subtitle")}</p>
          </div>
          <div className="space-y-4">
            <div>
              <Label htmlFor="email" className="label-caps">{t("auth.email")}</Label>
              <Input id="email" type="email" required autoComplete="email" placeholder="naam@bedrijf.nl" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email" className="rounded-sm mt-2" />
            </div>
            <div>
              <Label htmlFor="password" className="label-caps">{t("auth.password")}</Label>
              <Input id="password" type="password" required autoComplete="current-password" placeholder="••••••••" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password" className="rounded-sm mt-2" />
            </div>
          </div>
          <Button type="submit" disabled={busy} className="w-full rounded-sm bg-[#0052FF] hover:bg-[#0040CC]" data-testid="login-submit">
            {busy ? t("auth.loggingIn") : t("auth.login")}
          </Button>
          <div className="text-sm text-zinc-500">
            {t("auth.noAccount")}
          </div>
        </form>
        </div>
      </div>
    </div>
  );
}
